/**
 * 语言切换器 - 下拉菜单形式
 */

import { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from '../../useTranslation';
import { useLanguageNavigation } from '../../hooks/useLanguageNavigation';
import type { ActiveLanguage } from '../../types';
import type { SizeClasses } from './types';

interface DropdownProps {
  sizeClasses: SizeClasses;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function Dropdown({ sizeClasses, size = 'md', className = '' }: DropdownProps) {
  const { language, availableLanguages, languageName } = useTranslation();
  const { changeLanguage } = useLanguageNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭下拉
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (langCode: string) => {
    changeLanguage(langCode as ActiveLanguage);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 rounded-lg border border-[var(--y-border)]
          bg-[var(--y-surface-secondary)]/50 hover:bg-[var(--y-surface-secondary)] transition-colors
          ${sizeClasses[size]}
        `}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4 text-[var(--y-text-tertiary)]" />
        <span className="text-[var(--y-text-primary)]">{languageName}</span>
        <ChevronDown
          className={`w-4 h-4 text-[var(--y-text-tertiary)] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-48 py-1 rounded-lg bg-[var(--y-surface-primary)] border border-[var(--y-border)] shadow-xl z-50"
          role="listbox"
        >
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`
                w-full flex items-center justify-between px-4 py-2
                transition-colors text-left
                ${language === lang.code
                  ? 'text-[var(--y-accent)] bg-[var(--y-accent-light)]'
                  : 'text-[var(--y-text-secondary)] hover:bg-[var(--y-surface-secondary)]/50'
                }
              `}
              role="option"
              aria-selected={language === lang.code}
            >
              <span className="flex items-center gap-2">
                <span aria-hidden="true">{lang.flag}</span>
                <span>{lang.nativeName}</span>
              </span>
              {language === lang.code && (
                <Check className="w-4 h-4" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
